import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  AutoGraph as AutoGraphIcon,
  CalendarMonth as CalendarMonthIcon,
  DeleteOutline as DeleteOutlineIcon,
  EmailOutlined as EmailOutlinedIcon,
  HistoryEdu as HistoryEduIcon,
  PaidOutlined as PaidOutlinedIcon,
  RefreshRounded as RefreshRoundedIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { sendRequest } from '../../utils/dateHelpers';
import { canRetryHistoricalAnalysisPayment } from './historicalAnalysisPayment';
import Loading from '../Loading';
import { Skeleton } from '../ui';
import HistoricalAnalysisV2 from './HistoricalAnalysisV2';

const API_LIST_REQUESTS_URL = '/historical-analysis/requests';
const API_CREATE_REQUEST_URL = '/historical-analysis/requests';

const AUTO_REFRESH_STATUSES = new Set([
  'pending_estimate',
  'estimating',
  'payment_pending',
  'processing_queued',
  'processing'
]);

const ACTIVE_REQUEST_STATUSES = new Set([
  'pending_estimate',
  'estimating',
  'estimate_ready',
  'estimate_failed',
  'payment_pending',
  'payment_failed',
  'processing_queued',
  'processing',
  'processing_failed'
]);

const DELETABLE_STATUSES = new Set([
  'pending_estimate',
  'estimating',
  'estimate_ready',
  'estimate_failed',
  'payment_failed'
]);

const statusMetaMap = {
  pending_estimate: {
    title: 'We received your request',
    description:
      'Please give us a few minutes while we calculate the exact cost of extending your analysis history. We will email you as soon as the estimate is ready to review.',
    accent: 'from-sky-500 to-cyan-500',
    chip: 'bg-sky-50 text-sky-700 border-sky-200'
  },
  estimating: {
    title: 'Calculating your estimate',
    description:
      'We are scanning the requested commit history and calculating the one-time price for your historical analysis. We will email you as soon as the estimate is ready.',
    accent: 'from-sky-500 to-cyan-500',
    chip: 'bg-sky-50 text-sky-700 border-sky-200'
  },
  estimate_ready: {
    title: 'Your estimate is ready',
    description:
      'Review the total commit count, token volume, and one-time price. If everything looks right, you can start the historical analysis immediately.',
    accent: 'from-emerald-500 to-teal-500',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  payment_pending: {
    title: 'Payment submitted',
    description:
      'Paddle is confirming your payment. Historical analysis will start automatically after the payment is confirmed.',
    accent: 'from-sky-500 to-cyan-500',
    chip: 'bg-sky-50 text-sky-700 border-sky-200'
  },
  payment_failed: {
    title: 'We could not charge your card',
    description:
      'Please review your card details and available credit, then confirm again when you are ready.',
    accent: 'from-amber-500 to-orange-500',
    chip: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  estimate_failed: {
    title: 'Estimate could not be completed',
    description:
      'This request could not be priced successfully. You can delete it and create a new one.',
    accent: 'from-rose-500 to-red-500',
    chip: 'bg-rose-50 text-rose-700 border-rose-200'
  },
  processing_queued: {
    title: 'Historical analysis is starting',
    description:
      'Your payment was confirmed. We are preparing commit data for the historical analysis pipeline.',
    accent: 'from-indigo-500 to-blue-500',
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  },
  processing: {
    title: 'Historical analysis is in progress',
    description:
      'We are preparing commit data and running the historical analysis in phases, from the oldest commit to your current analysis start date.',
    accent: 'from-indigo-500 to-blue-500',
    chip: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  },
  processing_failed: {
    title: 'Historical analysis needs attention',
    description:
      'We hit an issue while processing this request. Please contact support if this state does not recover shortly.',
    accent: 'from-rose-500 to-red-500',
    chip: 'bg-rose-50 text-rose-700 border-rose-200'
  },
  completed: {
    title: 'Historical analysis completed',
    description:
      'Your older commits have been analyzed successfully and your analysis window has been extended.',
    accent: 'from-emerald-500 to-green-500',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};

const PANEL_CLASS =
  'rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900';
const PANEL_TIGHT_CLASS =
  'rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900';
const PANEL_SOFT_CLASS =
  'rounded-xl border border-slate-200 bg-slate-50/80 p-4 dark:border-slate-700 dark:bg-slate-950/40';
const PANEL_SUBTLE_CLASS =
  'rounded-xl border border-slate-200 bg-slate-50/70 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-950/40';
const BUTTON_PRIMARY_CLASS =
  'inline-flex min-h-11 items-center justify-center rounded-lg bg-[#3C50E0] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#3345c4] disabled:cursor-not-allowed disabled:opacity-60';
const BUTTON_SECONDARY_CLASS =
  'inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
const BADGE_LABEL_CLASS =
  'inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em]';
const SECTION_EYEBROW_CLASS =
  'text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500';
const SECTION_TITLE_CLASS =
  'mt-2 text-2xl font-semibold text-slate-900 dark:text-white';
const SECTION_DESCRIPTION_CLASS =
  'mt-3 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400';

const HistoricalAnalysisSkeleton = () => (
  <div
    className="min-h-[520px] space-y-6 bg-slate-50 p-4 dark:bg-slate-950 sm:p-6"
    role="status"
    aria-label="Loading historical analysis"
  >
    <div className="mx-auto max-w-270 space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="h-5 w-40 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <div className="mt-5 h-9 w-3/4 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <div className="mt-4 h-4 w-full max-w-2xl animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {[1, 2, 3, 4].map(item => (
          <div
            key={item}
            className="h-28 animate-pulse rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
          />
        ))}
      </div>
      <div className="h-64 animate-pulse rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900" />
    </div>
  </div>
);

export const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(date);
};

export const formatDateTimeUtc = value => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short'
  }).format(date);
};

const formatNumber = value =>
  new Intl.NumberFormat('en-US').format(Number(value || 0));

export const getUnanalyzedCommitsMessage = failedCommits => {
  const count = Math.max(0, Number(failedCommits || 0));
  if (!count) return '';

  return `${formatNumber(count)} ${
    count === 1 ? 'commit could' : 'commits could'
  } not be analyzed because their data could not be retrieved.`;
};

const HISTORICAL_PHASE_LABELS = {
  preprocess: 'Preparing commit data',
  effort: 'Effort analysis',
  categorization: 'Categorization',
  ai_leverage: 'AI leverage',
  finalizing: 'Finalizing results'
};

const toProgressCount = value => {
  const count = Number(value || 0);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
};

const isPipelinePhaseComplete = phase => {
  const total = toProgressCount(phase?.totalCommits);
  return (
    total > 0 &&
    toProgressCount(phase?.completedCommits) +
      toProgressCount(phase?.failedCommits) >=
      total
  );
};

export const getHistoricalPipelinePhases = pipelineProgress => {
  if (!pipelineProgress) return [];

  const totalCommits = toProgressCount(pipelineProgress.totalCommits);
  const preprocessedCommits = Math.min(
    totalCommits,
    toProgressCount(pipelineProgress.preprocessedCommits)
  );
  const effortComplete = isPipelinePhaseComplete(pipelineProgress.effort);
  const categorizationComplete = isPipelinePhaseComplete(
    pipelineProgress.categorization
  );
  const aiLeverageComplete = isPipelinePhaseComplete(
    pipelineProgress.aiLeverage
  );
  const preprocessInProgress = preprocessedCommits < totalCommits;

  const analysisPhase = ({ key, progress, waitingFor, isUnlocked }) => {
    const submittedCommits = Math.min(
      toProgressCount(progress?.totalCommits),
      toProgressCount(progress?.submittedCommits)
    );
    const phaseTotal = toProgressCount(progress?.totalCommits);

    return {
      key,
      label: HISTORICAL_PHASE_LABELS[key],
      current: submittedCommits,
      total: phaseTotal || totalCommits,
      waitingFor,
      isWaiting: !isUnlocked,
      isActive: isUnlocked && pipelineProgress.activePhase === key,
      isComplete: isPipelinePhaseComplete(progress),
      detail: isUnlocked
        ? `${formatNumber(submittedCommits)} of ${formatNumber(
            phaseTotal || totalCommits
          )} commits sent for analysis`
        : `Waiting for ${waitingFor}`
    };
  };

  return [
    {
      key: 'preprocess',
      label: HISTORICAL_PHASE_LABELS.preprocess,
      current: preprocessedCommits,
      total: totalCommits,
      isWaiting: false,
      isActive: preprocessInProgress,
      isComplete: !preprocessInProgress && totalCommits > 0,
      detail: `${formatNumber(preprocessedCommits)} of ${formatNumber(
        totalCommits
      )} commits prepared`
    },
    analysisPhase({
      key: 'effort',
      progress: pipelineProgress.effort,
      waitingFor: 'commit data preparation',
      isUnlocked:
        !preprocessInProgress ||
        toProgressCount(pipelineProgress?.effort?.submittedCommits) > 0
    }),
    analysisPhase({
      key: 'categorization',
      progress: pipelineProgress.categorization,
      waitingFor: 'effort analysis',
      isUnlocked: effortComplete
    }),
    analysisPhase({
      key: 'ai_leverage',
      progress: pipelineProgress.aiLeverage,
      waitingFor: 'effort and categorization analysis',
      isUnlocked: effortComplete && categorizationComplete
    }),
    {
      key: 'finalizing',
      label: HISTORICAL_PHASE_LABELS.finalizing,
      current: toProgressCount(pipelineProgress?.finalizing?.completedCommits),
      total:
        toProgressCount(pipelineProgress?.finalizing?.totalCommits) ||
        totalCommits,
      isWaiting: !aiLeverageComplete,
      isActive:
        aiLeverageComplete && pipelineProgress.activePhase === 'finalizing',
      isComplete:
        aiLeverageComplete &&
        isPipelinePhaseComplete(pipelineProgress.finalizing),
      detail: aiLeverageComplete
        ? `${formatNumber(
            pipelineProgress?.finalizing?.completedCommits
          )} of ${formatNumber(
            pipelineProgress?.finalizing?.totalCommits || totalCommits
          )} commits finalized`
        : 'Waiting for AI leverage analysis'
    }
  ];
};

const HistoricalPipelineProgress = ({ pipelineProgress }) => {
  const phases = getHistoricalPipelinePhases(pipelineProgress);
  const failedCommits = toProgressCount(
    pipelineProgress?.finalizing?.failedCommits
  );

  return (
    <div className={`mt-6 ${PANEL_SOFT_CLASS}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900 dark:text-white">
            Analysis pipeline
          </p>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Commit results are finalized after all analysis phases are complete.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {phases.map(phase => {
          const percentage = phase.total
            ? Math.min(100, (phase.current / phase.total) * 100)
            : 0;
          const titleClassName = phase.isActive
            ? 'text-[#3C50E0] dark:text-sky-300'
            : phase.isWaiting
              ? 'text-slate-500 dark:text-slate-400'
              : 'text-slate-900 dark:text-white';

          return (
            <div
              key={phase.key}
              className={`rounded-lg border p-3 ${
                phase.isActive
                  ? 'border-indigo-200 bg-indigo-50/60 dark:border-indigo-500/30 dark:bg-indigo-500/10'
                  : 'border-slate-200 bg-white/80 dark:border-slate-700 dark:bg-slate-950/40'
              }`}
            >
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <p className={`text-sm font-semibold ${titleClassName}`}>
                  {phase.label}
                </p>
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  {phase.isWaiting
                    ? phase.detail
                    : `${formatNumber(phase.current)} / ${formatNumber(
                        phase.total
                      )}`}
                </p>
              </div>
              {!phase.isWaiting && (
                <>
                  <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                    <div
                      className="h-full rounded-full bg-[#3C50E0] transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {phase.detail}
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>
      {failedCommits > 0 && (
        <p className="mt-4 text-sm text-amber-700 dark:text-amber-300">
          Unanalyzed commits: {formatNumber(failedCommits)}
        </p>
      )}
    </div>
  );
};

HistoricalPipelineProgress.propTypes = {
  pipelineProgress: PropTypes.object.isRequired
};

const formatCurrency = value =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const buildPaymentBreakdown = totalChargeAmountUsd => {
  const totalAmountUsd = Math.max(
    0,
    Math.ceil(Number(totalChargeAmountUsd || 0))
  );

  return {
    totalAmountUsd
  };
};

const getPaymentExplanation = (
  breakdown,
  { includeConfirmation = true } = {}
) => {
  const confirmationSuffix = includeConfirmation ? ' Do you confirm?' : '';

  if (!breakdown?.totalAmountUsd) {
    return includeConfirmation
      ? 'No payment is required for this historical analysis. Do you confirm starting the analysis?'
      : 'No payment is required for this historical analysis.';
  }

  return `We will charge ${formatCurrency(
    breakdown.totalAmountUsd
  )} through Paddle for this analysis.${confirmationSuffix}`;
};

const formatMonthKey = value => {
  const parts = parseMonthValue(value);
  if (!parts) return '';
  return `${MONTH_LABELS[parts.month - 1]} ${parts.year}`;
};

const formatCountdown = expiresAt => {
  if (!expiresAt) return '';
  const remainingMs = new Date(expiresAt).getTime() - Date.now();
  if (remainingMs <= 0) return 'Expired';

  const totalSeconds = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m ${seconds}s`;
  }

  return `${hours}h ${minutes}m ${seconds}s`;
};

const formatPlanName = value => {
  const text = String(value || '').trim();
  return text ? text.toUpperCase() : 'NOT CONFIGURED';
};

const toRequestDate = monthValue => (monthValue ? `${monthValue}-01` : '');

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const getUtcMonthStart = date =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0));

export const getPartialEndMonthValue = currentAnalyzeStartDate => {
  if (!currentAnalyzeStartDate) return '';
  const currentStartDate = new Date(currentAnalyzeStartDate);
  if (Number.isNaN(currentStartDate.getTime())) return '';
  const currentMonthStart = getUtcMonthStart(currentStartDate);
  return currentStartDate > currentMonthStart
    ? currentMonthStart.toISOString().slice(0, 7)
    : '';
};

export const getMonthMax = currentAnalyzeStartDate => {
  if (!currentAnalyzeStartDate) return '';
  const currentStartDate = new Date(currentAnalyzeStartDate);
  if (Number.isNaN(currentStartDate.getTime())) return '';
  const currentMonthStart = getUtcMonthStart(currentStartDate);
  if (currentStartDate > currentMonthStart) {
    return currentMonthStart.toISOString().slice(0, 7);
  }
  currentMonthStart.setUTCDate(0);
  return currentMonthStart.toISOString().slice(0, 7);
};

const parseMonthValue = value => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}$/.test(trimmed)) return null;
  const [yearText, monthText] = trimmed.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  if (!year || month < 1 || month > 12) return null;
  return { year, month };
};

const getMonthValue = (year, month) =>
  `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;

const getPickerYear = (selectedMonth, maxMonthValue) =>
  parseMonthValue(selectedMonth)?.year ||
  parseMonthValue(maxMonthValue)?.year ||
  new Date().getUTCFullYear();

const toMonthValue = value =>
  value && typeof value === 'string' ? value.slice(0, 7) : '';

const isAutoRefreshStatus = status => AUTO_REFRESH_STATUSES.has(status);

const isDeletableStatus = status => DELETABLE_STATUSES.has(status);

const isWaitingForEstimate = status =>
  status === 'pending_estimate' || status === 'estimating';

export const getHistoricalAnalysisConfirmationState = (
  request,
  paymentBreakdown
) => {
  const totalAmountUsd = Number(
    paymentBreakdown?.totalAmountUsd ??
      request?.payment?.chargeAmountUsd ??
      request?.estimate?.chargeAmountUsd ??
      0
  );
  const analysisStarted =
    request?.status === 'processing_queued' ||
    (request?.status !== 'payment_pending' && totalAmountUsd <= 0);

  if (analysisStarted) {
    const paymentWasRequired = totalAmountUsd > 0;
    return {
      status: 'success',
      title: paymentWasRequired
        ? 'Payment confirmed, analysis started'
        : 'Analysis started',
      description: paymentWasRequired
        ? 'Your Paddle payment was confirmed. Historical analysis has started and will continue in the background.'
        : 'No payment was required. Historical analysis has started and will continue in the background.'
    };
  }

  return {
    status: 'submitted',
    title: 'Payment submitted',
    description:
      'Paddle is confirming your payment. You can close this window; historical analysis will start automatically after confirmation.'
  };
};

const HistoricalAnalysis = () => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteRequestId, setDeleteRequestId] = useState('');
  const [confirmRequestId, setConfirmRequestId] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [pickerYear, setPickerYear] = useState(() =>
    new Date().getUTCFullYear()
  );
  const [offerCountdown, setOfferCountdown] = useState('');
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    mode: '',
    requestId: '',
    requestedStartDate: '',
    chargeAmountUsd: 0,
    paymentBreakdown: null,
    requestedStartLabel: '',
    status: 'idle',
    title: '',
    description: '',
    errorMessage: ''
  });
  const [payload, setPayload] = useState({
    flowVersion: 0,
    currentAnalyzeStartDate: '',
    currentPlanName: '',
    requests: [],
    offers: [],
    offerBatch: null,
    canCreateManualRequest: true
  });

  const requests = payload?.requests || [];
  const offerBatch = payload?.offerBatch || null;

  const activeRequest = useMemo(
    () =>
      requests.find(request => ACTIVE_REQUEST_STATUSES.has(request.status)) ||
      null,
    [requests]
  );

  const recentRequests = useMemo(
    () =>
      requests.filter(request =>
        activeRequest ? request.id !== activeRequest.id : true
      ),
    [activeRequest, requests]
  );

  const activeOfferBatch = useMemo(() => {
    if (!offerBatch) return null;
    return ['preparing', 'ready'].includes(offerBatch.status)
      ? offerBatch
      : null;
  }, [offerBatch]);

  const offerPricingCacheStatus = String(
    activeOfferBatch?.pricingCacheStatus || ''
  ).toLowerCase();
  const isLegacyOfferPreparing =
    !activeRequest &&
    Boolean(activeOfferBatch) &&
    activeOfferBatch?.status === 'ready' &&
    !offerPricingCacheStatus &&
    payload.canCreateManualRequest === false &&
    (payload.offers || []).length === 0;
  const isOfferPreparing =
    isLegacyOfferPreparing ||
    (!activeRequest &&
      Boolean(activeOfferBatch) &&
      (activeOfferBatch?.status === 'preparing' ||
        ['pending_estimate', 'estimating'].includes(offerPricingCacheStatus)));
  const hasActivePromotion =
    !activeRequest &&
    activeOfferBatch?.status === 'ready' &&
    offerPricingCacheStatus === 'ready';

  const selectedStatusMeta =
    statusMetaMap[activeRequest?.status] || statusMetaMap.pending_estimate;

  const heroMeta = useMemo(() => {
    if (activeRequest) return selectedStatusMeta;
    if (hasActivePromotion) {
      return {
        accent: 'from-amber-500 via-orange-500 to-rose-500'
      };
    }
    if (isOfferPreparing) {
      return {
        accent: 'from-sky-500 via-cyan-500 to-blue-600'
      };
    }
    return {
      accent: 'from-cyan-600 via-blue-600 to-indigo-600'
    };
  }, [activeRequest, hasActivePromotion, isOfferPreparing, selectedStatusMeta]);

  const maxMonthValue = useMemo(
    () => getMonthMax(payload.currentAnalyzeStartDate),
    [payload.currentAnalyzeStartDate]
  );
  const partialEndMonthValue = useMemo(
    () => getPartialEndMonthValue(payload.currentAnalyzeStartDate),
    [payload.currentAnalyzeStartDate]
  );
  const promoDefaultMonthValue = useMemo(
    () => toMonthValue(activeOfferBatch?.defaultRequestedStartDate),
    [activeOfferBatch?.defaultRequestedStartDate]
  );
  const promoDiscountThresholdMonthValue = useMemo(
    () => toMonthValue(activeOfferBatch?.discountEligibleStartDate),
    [activeOfferBatch?.discountEligibleStartDate]
  );
  const promoEarliestMonthValue = useMemo(
    () => toMonthValue(activeOfferBatch?.earliestRequestedStartDate),
    [activeOfferBatch?.earliestRequestedStartDate]
  );

  const fetchRequests = async (silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await sendRequest(API_LIST_REQUESTS_URL, 'GET');
      setPayload({
        flowVersion: Number(response?.flowVersion || 0),
        ...(response?.credit ? { credit: response.credit } : {}),
        currentAnalyzeStartDate: response?.currentAnalyzeStartDate || '',
        currentPlanName: response?.currentPlanName || '',
        requests: response?.requests || [],
        offers: response?.offers || [],
        offerBatch: response?.offerBatch || null,
        canCreateManualRequest: response?.canCreateManualRequest !== false
      });
    } catch (error) {
      toast.error(
        error?.message || 'Failed to load historical analysis requests.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const closeConfirmationModal = () => {
    if (confirmationModal.status === 'processing') return;
    setConfirmationModal({
      isOpen: false,
      mode: '',
      requestId: '',
      requestedStartDate: '',
      chargeAmountUsd: 0,
      paymentBreakdown: null,
      requestedStartLabel: '',
      status: 'idle',
      title: '',
      description: '',
      errorMessage: ''
    });
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    setPickerYear(getPickerYear(selectedMonth, maxMonthValue));
  }, [selectedMonth, maxMonthValue]);

  useEffect(() => {
    if (!hasActivePromotion || !promoDefaultMonthValue) {
      return;
    }
    setSelectedMonth(currentValue => {
      if (!currentValue) {
        return promoDefaultMonthValue;
      }
      if (
        (promoEarliestMonthValue && currentValue < promoEarliestMonthValue) ||
        (maxMonthValue && currentValue > maxMonthValue)
      ) {
        return promoDefaultMonthValue;
      }
      return currentValue;
    });
  }, [
    hasActivePromotion,
    maxMonthValue,
    promoDefaultMonthValue,
    promoEarliestMonthValue
  ]);

  useEffect(() => {
    const shouldAutoRefresh =
      !activeRequest ||
      (activeRequest && isAutoRefreshStatus(activeRequest.status)) ||
      isOfferPreparing;
    if (!shouldAutoRefresh) return;

    const intervalId = window.setInterval(() => {
      fetchRequests(true);
    }, 15000);

    return () => window.clearInterval(intervalId);
  }, [activeRequest, isOfferPreparing]);

  useEffect(() => {
    if (!hasActivePromotion || !activeOfferBatch?.expiresAt) {
      setOfferCountdown('');
      return undefined;
    }

    const updateCountdown = () =>
      setOfferCountdown(formatCountdown(activeOfferBatch.expiresAt));

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(intervalId);
  }, [activeOfferBatch?.expiresAt, hasActivePromotion]);

  const handleCreateRequest = async () => {
    const requestedStartDate = toRequestDate(selectedMonth);

    if (!requestedStartDate) {
      toast.warning('Please select a month to continue.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await sendRequest(API_CREATE_REQUEST_URL, 'POST', {
        requestedStartDate
      });
      toast.success(
        response?.message ||
          'Your historical analysis request has been created.'
      );
      await fetchRequests(true);
    } catch (error) {
      const existingRequest = error?.response?.data?.request;
      if (existingRequest) {
        setPayload(previous => ({
          ...previous,
          requests: [
            existingRequest,
            ...previous.requests.filter(item => item.id !== existingRequest.id)
          ]
        }));
      }
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to create historical analysis request.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRequest = async requestId => {
    const shouldDelete = window.confirm(
      'Delete this historical analysis request and its temporary calculation data?'
    );

    if (!shouldDelete) return;

    try {
      setDeleteRequestId(requestId);
      await sendRequest(`${API_CREATE_REQUEST_URL}/${requestId}`, 'DELETE');
      toast.success('Historical analysis request deleted.');
      await fetchRequests(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to delete historical analysis request.'
      );
    } finally {
      setDeleteRequestId('');
    }
  };

  const getProjectedPaymentBreakdown = chargeAmountUsd =>
    buildPaymentBreakdown(chargeAmountUsd);

  const getRequestPaymentBreakdown = request => {
    const chargeAmountUsd =
      request?.estimate?.chargeAmountUsd ||
      request?.payment?.chargeAmountUsd ||
      0;
    if (
      request?.status === 'estimate_ready' ||
      request?.status === 'payment_failed'
    ) {
      return getProjectedPaymentBreakdown(chargeAmountUsd);
    }

    return getProjectedPaymentBreakdown(chargeAmountUsd);
  };

  const openDirectStartConfirmation = () => {
    const requestedStartDate = toRequestDate(selectedMonth);

    if (!requestedStartDate) {
      toast.warning('Please select a month to continue.');
      return;
    }

    const chargeAmountUsd = Number(selectedMonthPreview?.chargeAmountUsd || 0);
    const paymentBreakdown = getProjectedPaymentBreakdown(chargeAmountUsd);

    setConfirmationModal({
      isOpen: true,
      mode: 'direct_start',
      requestId: '',
      requestedStartDate,
      chargeAmountUsd,
      paymentBreakdown,
      requestedStartLabel: selectedMonthLabel || requestedStartDate,
      status: 'confirm',
      title: 'Review and confirm payment',
      description: getPaymentExplanation(paymentBreakdown),
      errorMessage: ''
    });
  };

  const openRequestConfirmation = request => {
    const chargeAmountUsd = Number(
      request?.estimate?.chargeAmountUsd ||
        request?.payment?.chargeAmountUsd ||
        0
    );
    const paymentBreakdown = getRequestPaymentBreakdown(request);

    setConfirmationModal({
      isOpen: true,
      mode: 'request_charge',
      requestId: request?.id || '',
      requestedStartDate: '',
      chargeAmountUsd,
      paymentBreakdown,
      requestedStartLabel: formatDate(request?.requestedStartDate),
      status: 'confirm',
      title: 'Review and confirm payment',
      description: getPaymentExplanation(paymentBreakdown),
      errorMessage: ''
    });
  };

  const handleConfirmPayment = async () => {
    if (confirmationModal.status !== 'confirm') return;

    setConfirmationModal(previous => ({
      ...previous,
      status: 'processing',
      title: 'Payment is being processed',
      description:
        'We are submitting the full charge to Paddle now. Historical analysis will start automatically as soon as the payment succeeds.',
      errorMessage: ''
    }));

    try {
      let confirmResponse = null;
      if (confirmationModal.mode === 'direct_start') {
        setSubmitting(true);
        const createResponse = await sendRequest(
          API_CREATE_REQUEST_URL,
          'POST',
          {
            requestedStartDate: confirmationModal.requestedStartDate
          }
        );
        const createdRequest = createResponse?.request || null;

        if (createdRequest?.id && createdRequest?.status === 'estimate_ready') {
          confirmResponse = await sendRequest(
            `${API_CREATE_REQUEST_URL}/${createdRequest.id}/confirm-charge`,
            'POST'
          );
        }

        if (!confirmResponse) {
          throw new Error(
            'The estimate is not ready yet. Please refresh and try again in a moment.'
          );
        }
      } else {
        setConfirmRequestId(confirmationModal.requestId || '');
        confirmResponse = await sendRequest(
          `${API_CREATE_REQUEST_URL}/${confirmationModal.requestId}/confirm-charge`,
          'POST'
        );
      }

      const paymentBreakdown =
        confirmResponse?.paymentBreakdown || confirmationModal.paymentBreakdown;
      const confirmationState = getHistoricalAnalysisConfirmationState(
        confirmResponse?.request,
        paymentBreakdown
      );

      await fetchRequests(true);
      setConfirmationModal(previous => ({
        ...previous,
        ...confirmationState,
        paymentBreakdown,
        chargeAmountUsd:
          paymentBreakdown?.totalAmountUsd || previous.chargeAmountUsd
      }));
    } catch (error) {
      const existingRequest =
        error?.response?.data?.request ||
        error?.response?.data?.existingRequest;
      if (existingRequest) {
        setPayload(previous => ({
          ...previous,
          requests: [
            existingRequest,
            ...previous.requests.filter(item => item.id !== existingRequest.id)
          ]
        }));
      }

      await fetchRequests(true);
      setConfirmationModal(previous => ({
        ...previous,
        status: 'error',
        title: 'We could not confirm the payment',
        description:
          'Please check your card details and available limit, then try again.',
        paymentBreakdown:
          error?.response?.data?.paymentBreakdown || previous.paymentBreakdown,
        errorMessage:
          error?.response?.data?.message ||
          error?.message ||
          'The payment could not be completed.'
      }));
    } finally {
      setSubmitting(false);
      setConfirmRequestId('');
    }
  };

  const renderEstimateMetrics = request => {
    const paymentBreakdown = getRequestPaymentBreakdown(request);
    const shouldShowPaymentBreakdown =
      request?.status === 'estimate_ready' ||
      request?.status === 'payment_pending' ||
      request?.status === 'payment_failed' ||
      request?.status === 'processing_queued' ||
      request?.status === 'processing' ||
      request?.status === 'completed';

    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className={PANEL_TIGHT_CLASS}>
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
              Total Commits
            </p>
            <p className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
              {formatNumber(request?.estimate?.commitCount)}
            </p>
          </div>
          <div className={PANEL_TIGHT_CLASS}>
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
              Total Tokens
            </p>
            <p className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
              {formatNumber(request?.estimate?.totalTokens)}
            </p>
          </div>
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 shadow-sm ring-1 ring-indigo-100 dark:border-indigo-800/70 dark:bg-indigo-950/30 dark:ring-indigo-900/50">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
              One-Time Price
            </p>
            <p className="mt-3 text-2xl font-semibold text-indigo-700 dark:text-indigo-200">
              {formatCurrency(request?.estimate?.chargeAmountUsd)}
            </p>
          </div>
        </div>

        {shouldShowPaymentBreakdown && paymentBreakdown?.totalAmountUsd > 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            The full {formatCurrency(paymentBreakdown.totalAmountUsd)} one-time
            charge is collected through Paddle.
          </p>
        )}
      </div>
    );
  };

  const renderOfferPreparationState = () => {
    if (!isOfferPreparing) {
      return null;
    }

    const isOfferEstimating = offerPricingCacheStatus === 'estimating';
    const waitingLabel = isOfferEstimating
      ? 'Scanning commit history and pricing your offer'
      : 'Offer request received and queued';
    const waitingSteps = [
      {
        label: 'Request received and queued',
        state: isOfferEstimating ? 'done' : 'active'
      },
      {
        label: 'Commit history is being scanned',
        state: isOfferEstimating ? 'active' : 'upcoming'
      },
      {
        label: 'Review and approve',
        state: 'upcoming'
      }
    ];

    return (
      <div className="mt-6 rounded-xl border border-sky-200 bg-sky-50/70 p-6 shadow-sm dark:border-sky-900/60 dark:bg-sky-950/20">
        <div className="relative">
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div
                className={`${BADGE_LABEL_CLASS} border-sky-200 bg-white text-sky-700 dark:border-sky-800/70 dark:bg-slate-900 dark:text-sky-200`}
              >
                <span className="mr-3 inline-flex h-2.5 w-2.5 rounded-full bg-[#259AE6] animate-pulse" />
                {waitingLabel}
              </div>

              <div className="mt-5 flex items-center gap-4">
                <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#259AE6] text-white">
                  <span className="absolute inset-0 rounded-xl border border-white/30 animate-ping" />
                  <AutoGraphIcon className="relative" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-semibold leading-tight text-slate-900 dark:text-white">
                    We are building your discounted historical analysis offers
                  </p>
                </div>
              </div>

              <div className="mt-4 flex items-start gap-3 rounded-xl border border-sky-200 bg-white px-4 py-4 dark:border-sky-800/70 dark:bg-slate-900 sm:ml-16">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200">
                  <EmailOutlinedIcon />
                </div>
                <p className="text-sm font-semibold leading-6 text-slate-800 dark:text-slate-100">
                  We will send you an email as soon as your discounted offers
                  are ready. Depending on your requested date range and the size
                  of your codebase, this can take anywhere from a few minutes to
                  a few hours. You can come back later to review the offer and
                  approve the one-time charge.
                </p>
              </div>

              <div className="mt-6 rounded-xl border border-sky-200 bg-white p-4 dark:border-sky-800/60 dark:bg-slate-900">
                <div className="space-y-0">
                  {waitingSteps.map((step, index) => {
                    const isActive = step.state === 'active';
                    const isDone = step.state === 'done';
                    const isLast = index === waitingSteps.length - 1;

                    return (
                      <div
                        key={step.label}
                        className={`relative flex gap-4 ${
                          isLast ? '' : 'pb-5'
                        }`}
                      >
                        <div className="relative flex w-10 shrink-0 justify-center">
                          {!isLast && (
                            <div
                              className={`absolute top-10 h-[calc(100%-2rem)] w-0.5 ${
                                isDone
                                  ? 'bg-emerald-300 dark:bg-emerald-700'
                                  : isActive
                                    ? 'bg-gradient-to-b from-sky-400 via-cyan-400 to-slate-200 dark:to-slate-700'
                                    : 'bg-slate-200 dark:bg-slate-700'
                              }`}
                            />
                          )}
                          <div
                            className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold ${
                              isActive
                                ? 'border-sky-400 bg-[#259AE6] text-white'
                                : isDone
                                  ? 'border-emerald-400 bg-[#10B981] text-white'
                                  : 'border-slate-300 bg-white text-slate-500 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-300'
                            }`}
                          >
                            {isActive && (
                              <span className="absolute inset-0 rounded-full border border-sky-300 animate-ping" />
                            )}
                            <span className="relative">{index + 1}</span>
                          </div>
                        </div>

                        <div
                          className={`flex-1 rounded-xl border px-4 py-4 ${
                            isActive
                              ? 'border-sky-200 bg-sky-50/90 dark:border-sky-800/70 dark:bg-sky-950/30'
                              : isDone
                                ? 'border-emerald-200 bg-emerald-50/80 dark:border-emerald-900/70 dark:bg-emerald-950/20'
                                : 'border-slate-200 bg-white/70 dark:border-slate-800 dark:bg-slate-950/40'
                          }`}
                        >
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              {step.label}
                            </p>
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] ${
                                isActive
                                  ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200'
                                  : isDone
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200'
                                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300'
                              }`}
                            >
                              {isActive
                                ? 'In progress'
                                : isDone
                                  ? 'Completed'
                                  : 'Waiting'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="hidden lg:block lg:min-w-0" />
          </div>
        </div>
      </div>
    );
  };

  const renderOfferShowcase = () => {
    if (!hasActivePromotion) {
      return null;
    }

    return (
      <div className={PANEL_CLASS}>
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-6 shadow-sm dark:border-amber-900/50 dark:bg-amber-950/10">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <div
                className={`${BADGE_LABEL_CLASS} border-amber-200 bg-white text-amber-700 dark:border-amber-800/70 dark:bg-slate-900 dark:text-amber-200`}
              >
                Limited-Time Historical Analysis Offer
              </div>
              <h3 className="mt-5 text-2xl font-semibold leading-tight text-slate-900 dark:text-white">
                Unlock 50% off when your request starts 6 months back or earlier
              </h3>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                This promotional window stays open for 7 days. During that time,
                requests that start on or before{' '}
                <span className="font-semibold text-slate-900 dark:text-white">
                  {formatDate(activeOfferBatch?.discountEligibleStartDate)}
                </span>{' '}
                get a 50% discount, and you can go back as far as{' '}
                <span className="font-semibold text-slate-900 dark:text-white">
                  {formatDate(activeOfferBatch?.earliestRequestedStartDate)}
                </span>
                .
              </p>
            </div>

            <div className="rounded-xl border border-rose-200 bg-white px-5 py-4 shadow-sm dark:border-rose-800/60 dark:bg-slate-900 xl:min-h-[176px] xl:w-[220px]">
              <p className="text-xs font-semibold uppercase tracking-[0.26em] text-rose-500 dark:text-rose-300">
                Offer Countdown
              </p>
              <p className="mt-2 min-h-[5.5rem] text-3xl font-semibold leading-tight text-slate-900 dark:text-white">
                {offerCountdown || 'Expired'}
              </p>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Expires on {formatDate(activeOfferBatch?.expiresAt)}
              </p>
            </div>
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <div className="rounded-xl border border-amber-200 bg-white p-5 shadow-sm dark:border-amber-800/60 dark:bg-slate-900">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-500 dark:text-amber-300">
                Default Start
              </p>
              <p className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">
                {formatDate(activeOfferBatch?.defaultRequestedStartDate)}
              </p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-white p-5 shadow-sm dark:border-amber-800/60 dark:bg-slate-900">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-500 dark:text-amber-300">
                Discount Rule
              </p>
              <p className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">
                50% off for 6+ months
              </p>
            </div>
            <div className="rounded-xl border border-amber-200 bg-white p-5 shadow-sm dark:border-amber-800/60 dark:bg-slate-900">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-500 dark:text-amber-300">
                Earliest Allowed
              </p>
              <p className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">
                {formatDate(activeOfferBatch?.earliestRequestedStartDate)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWaitingState = request => {
    if (!isWaitingForEstimate(request?.status)) {
      return null;
    }

    const waitingLabel =
      request.status === 'estimating'
        ? 'Scanning history and calculating price'
        : 'Request received and queued';

    const waitingSteps = [
      {
        label: 'Request received and queued',
        state: request.status === 'pending_estimate' ? 'active' : 'done'
      },
      {
        label: 'Commit history is being scanned',
        state: request.status === 'estimating' ? 'active' : 'upcoming'
      },
      {
        label: 'Review and approve',
        state: 'upcoming'
      }
    ];

    return (
      <div className="mt-6 rounded-xl border border-sky-200 bg-sky-50/70 p-6 shadow-sm dark:border-sky-900/60 dark:bg-sky-950/20">
        <div className="relative">
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <div
                className={`${BADGE_LABEL_CLASS} border-sky-200 bg-white text-sky-700 dark:border-sky-800/70 dark:bg-slate-900 dark:text-sky-200`}
              >
                <span className="mr-3 inline-flex h-2.5 w-2.5 rounded-full bg-[#259AE6] animate-pulse" />
                {waitingLabel}
              </div>

              <div className="mt-5 flex items-center gap-4">
                <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#259AE6] text-white">
                  <span className="absolute inset-0 rounded-xl border border-white/30 animate-ping" />
                  <ScheduleIcon className="relative" />
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-semibold leading-tight text-slate-900 dark:text-white">
                    Your estimate is being prepared
                  </p>
                </div>
              </div>

              <div className="mt-4 flex items-start gap-3 rounded-xl border border-sky-200 bg-white px-4 py-4 dark:border-sky-800/70 dark:bg-slate-900 sm:ml-16">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200">
                  <EmailOutlinedIcon />
                </div>
                <p className="text-sm font-semibold leading-6 text-slate-800 dark:text-slate-100">
                  We will send you an email as soon as your estimate is ready.
                  Depending on your requested date range and the size of your
                  codebase, this can take anywhere from a few minutes to a few
                  hours. You can come back later to review it and approve the
                  one-time charge.
                </p>
              </div>

              <div className="mt-6 rounded-xl border border-sky-200 bg-white p-4 dark:border-sky-800/60 dark:bg-slate-900">
                <div className="space-y-0">
                  {waitingSteps.map((step, index) => {
                    const isActive = step.state === 'active';
                    const isDone = step.state === 'done';
                    const isLast = index === waitingSteps.length - 1;

                    return (
                      <div
                        key={step.label}
                        className={`relative flex gap-4 ${
                          isLast ? '' : 'pb-5'
                        }`}
                      >
                        <div className="relative flex w-10 shrink-0 justify-center">
                          {!isLast && (
                            <div
                              className={`absolute top-10 h-[calc(100%-2rem)] w-0.5 ${
                                isDone
                                  ? 'bg-emerald-300 dark:bg-emerald-700'
                                  : isActive
                                    ? 'bg-gradient-to-b from-sky-400 via-cyan-400 to-slate-200 dark:to-slate-700'
                                    : 'bg-slate-200 dark:bg-slate-700'
                              }`}
                            />
                          )}
                          <div
                            className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border text-sm font-semibold ${
                              isActive
                                ? 'border-sky-400 bg-[#259AE6] text-white'
                                : isDone
                                  ? 'border-emerald-400 bg-[#10B981] text-white'
                                  : 'border-slate-300 bg-white text-slate-500 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-300'
                            }`}
                          >
                            {isActive && (
                              <span className="absolute inset-0 rounded-full border border-sky-300 animate-ping" />
                            )}
                            <span className="relative">{index + 1}</span>
                          </div>
                        </div>

                        <div
                          className={`flex-1 rounded-xl border px-4 py-4 ${
                            isActive
                              ? 'border-sky-200 bg-sky-50/90 dark:border-sky-800/70 dark:bg-sky-950/30'
                              : isDone
                                ? 'border-emerald-200 bg-emerald-50/80 dark:border-emerald-900/70 dark:bg-emerald-950/20'
                                : 'border-slate-200 bg-white/70 dark:border-slate-800 dark:bg-slate-950/40'
                          }`}
                        >
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              {step.label}
                            </p>
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] ${
                                isActive
                                  ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200'
                                  : isDone
                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200'
                                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300'
                              }`}
                            >
                              {isActive
                                ? 'In progress'
                                : isDone
                                  ? 'Completed'
                                  : 'Waiting'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="hidden lg:block lg:min-w-0" />
          </div>
        </div>
      </div>
    );
  };

  const maxMonthParts = parseMonthValue(maxMonthValue);
  const canGoNextYear = maxMonthParts ? pickerYear < maxMonthParts.year : false;
  const shouldShowRequestOffer = Boolean(selectedMonth);
  const selectedMonthLabel = selectedMonth ? formatMonthKey(selectedMonth) : '';
  const selectedMonthPreview =
    activeOfferBatch?.monthPreviews?.[selectedMonth] || null;
  const shouldShowDiscountRow = hasActivePromotion;
  const isPromoDiscountApplied = Boolean(
    selectedMonthPreview?.isDiscountApplied ||
    (hasActivePromotion &&
      selectedMonth &&
      promoDiscountThresholdMonthValue &&
      selectedMonth <= promoDiscountThresholdMonthValue)
  );
  const renderRequestActions = request => {
    if (!request) return null;

    const isDeleteLoading = deleteRequestId === request.id;
    const isConfirmLoading = confirmRequestId === request.id;

    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        {canRetryHistoricalAnalysisPayment(request.status) && (
          <button
            type="button"
            onClick={() => openRequestConfirmation(request)}
            disabled={isConfirmLoading}
            className={BUTTON_PRIMARY_CLASS}
          >
            <PaidOutlinedIcon className="mr-2" fontSize="small" />
            {isConfirmLoading ? (
              <Loading type="inline" />
            ) : (
              'Review Payment and Start Analysis'
            )}
          </button>
        )}
        {isDeletableStatus(request.status) && (
          <button
            type="button"
            onClick={() => handleDeleteRequest(request.id)}
            disabled={isDeleteLoading}
            className={BUTTON_SECONDARY_CLASS}
          >
            <DeleteOutlineIcon className="mr-2" fontSize="small" />
            {isDeleteLoading ? <Loading type="inline" /> : 'Delete Request'}
          </button>
        )}
        <button
          type="button"
          onClick={() => fetchRequests(true)}
          disabled={refreshing}
          className={BUTTON_SECONDARY_CLASS}
        >
          <RefreshRoundedIcon className="mr-2" fontSize="small" />
          {refreshing ? <Loading type="inline" /> : 'Refresh'}
        </button>
      </div>
    );
  };

  if (loading) {
    return <HistoricalAnalysisSkeleton />;
  }

  const hasV2Request = payload.requests.some(
    request => Number(request.flowVersion) === 2
  );
  const hasActiveLegacyRequest = payload.requests.some(
    request =>
      Number(request.flowVersion || 1) !== 2 &&
      ACTIVE_REQUEST_STATUSES.has(request.status)
  );
  if (
    !hasActiveLegacyRequest &&
    (hasV2Request || Number(payload.flowVersion) === 2)
  ) {
    return <HistoricalAnalysisV2 initialPayload={payload} />;
  }

  const isModalProcessing = confirmationModal.status === 'processing';
  const isModalSubmitted = confirmationModal.status === 'submitted';
  const isModalSuccess = confirmationModal.status === 'success';
  const isModalConfirm = confirmationModal.status === 'confirm';

  return (
    <div className="mx-auto max-w-270 space-y-6">
      {confirmationModal.isOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/45 backdrop-blur-md"
            aria-label="Close confirmation dialog"
            onClick={closeConfirmationModal}
            disabled={isModalProcessing}
          />
          <div className="relative z-10 w-full max-w-xl overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900">
            <div className="border-b border-slate-200 bg-slate-900 px-6 py-6 text-white dark:border-slate-700">
              <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-white/80">
                Historical Analysis
              </div>
              <h3 className="mt-4 text-2xl font-semibold leading-tight">
                {confirmationModal.title ||
                  'Confirm Payment and Start Analysis'}
              </h3>
              <p className="mt-3 max-w-lg text-sm leading-6 text-white/75">
                {confirmationModal.description ||
                  'We are preparing your payment and analysis request.'}
              </p>
            </div>

            <div className="px-6 py-6">
              <div className={PANEL_SOFT_CLASS}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                      Selected Start Month
                    </p>
                    <p className="mt-2 text-xl font-semibold text-slate-900 dark:text-white">
                      {confirmationModal.requestedStartLabel || '-'}
                    </p>
                  </div>
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-right ring-1 ring-indigo-100 dark:border-indigo-800/70 dark:bg-indigo-950/30 dark:ring-indigo-900/50">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-500 dark:text-indigo-300">
                      One-Time Price
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-indigo-700 dark:text-indigo-200">
                      {formatCurrency(confirmationModal.chargeAmountUsd)}
                    </p>
                  </div>
                </div>

                <div className="mt-5 rounded-xl border border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-900">
                  {isModalConfirm ? (
                    <div className="flex items-start gap-3">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200">
                        <PaidOutlinedIcon fontSize="small" />
                      </span>
                      <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                        {getPaymentExplanation(
                          confirmationModal.paymentBreakdown
                        )}
                      </p>
                    </div>
                  ) : isModalProcessing ? (
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200">
                        <Skeleton className="h-5 w-5 rounded-sm bg-current/30" />
                      </span>
                      <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                        We are submitting the full charge to Paddle now. Please
                        keep this window open while the payment request is
                        submitted.
                      </p>
                    </div>
                  ) : isModalSubmitted ? (
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-200">
                        <ScheduleIcon fontSize="small" />
                      </span>
                      <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                        Your payment request was submitted to Paddle. We are
                        waiting for confirmation and will not create a second
                        charge while this payment is pending.
                      </p>
                    </div>
                  ) : isModalSuccess ? (
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200">
                        <PaidOutlinedIcon fontSize="small" />
                      </span>
                      <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                        {confirmationModal.description}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-200">
                        <PaidOutlinedIcon fontSize="small" />
                      </span>
                      <div>
                        <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                          We could not receive the payment. Please check your
                          card details and available limit, then try again.
                        </p>
                        {confirmationModal.errorMessage ? (
                          <p className="mt-2 text-sm font-medium text-rose-600 dark:text-rose-300">
                            {confirmationModal.errorMessage}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex flex-col justify-end gap-3 sm:flex-row">
                {isModalConfirm ? (
                  <>
                    <button
                      type="button"
                      onClick={closeConfirmationModal}
                      className={`${BUTTON_SECONDARY_CLASS} min-w-32`}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmPayment}
                      className={`${BUTTON_PRIMARY_CLASS} min-w-44`}
                    >
                      Confirm and Start
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={closeConfirmationModal}
                    disabled={isModalProcessing}
                    className={`${BUTTON_SECONDARY_CLASS} min-w-42`}
                  >
                    {isModalProcessing ? (
                      <Loading type="inline" />
                    ) : isModalSuccess || isModalSubmitted ? (
                      'Close'
                    ) : (
                      'Close and Try Again'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={`relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br ${heroMeta.accent} p-6 text-white shadow-sm dark:border-slate-700`}
      >
        <div className="absolute right-0 top-0 h-36 w-36 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-28 w-28 rounded-full bg-slate-900/10 blur-3xl" />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="inline-flex items-center rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em]">
              <HistoryEduIcon className="mr-2" fontSize="inherit" />
              Historical Analysis
            </div>
            <h2 className="mt-4 text-3xl font-semibold leading-tight">
              Extend your analysis window into older commit history.
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-white/85">
              {hasActivePromotion
                ? 'Your 7-day promotional window is live. Select a start month to see the discounted one-time charge, then confirm to charge your card and start historical analysis immediately.'
                : isOfferPreparing
                  ? 'We are preparing your promotional historical analysis pricing cache in the background. We will email you as soon as your offer is ready to use.'
                  : 'Use your current plan to request a historical analysis extension. We will calculate the total commit volume, token usage, and one-time price before any charge is attempted.'}
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:min-w-100">
            <div className="flex flex-col rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
              <p className="min-h-[3.5rem] text-xs uppercase tracking-[0.24em] text-white/70">
                Current Plan
              </p>
              <p className="mt-2 text-lg font-semibold">
                {formatPlanName(payload.currentPlanName)}
              </p>
            </div>
            <div className="flex flex-col rounded-xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
              <p className="min-h-[3.5rem] text-xs uppercase tracking-[0.24em] text-white/70">
                Current Analysis Start
              </p>
              <p className="mt-2 text-lg font-semibold">
                {formatDateTimeUtc(payload.currentAnalyzeStartDate)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {renderOfferPreparationState()}

      {renderOfferShowcase()}

      {!activeRequest &&
        !isOfferPreparing &&
        payload.canCreateManualRequest && (
          <div className={PANEL_CLASS}>
            <div>
              <p className={SECTION_EYEBROW_CLASS}>Request</p>
              <p className={SECTION_TITLE_CLASS}>Request Historical Analysis</p>
              <p className={SECTION_DESCRIPTION_CLASS}>
                {hasActivePromotion
                  ? 'Select the month you want historical analysis to start from. Your 7-day promotional offer is active now.'
                  : 'Select the month you want historical analysis to start from.'}
              </p>
            </div>

            <div
              className={`mt-6 grid items-stretch gap-4 ${shouldShowRequestOffer ? 'lg:grid-cols-[minmax(0,1fr)_290px]' : ''}`}
            >
              <div className={PANEL_TIGHT_CLASS}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <label className="inline-flex items-center text-sm font-semibold text-slate-800 dark:text-slate-100">
                      <CalendarMonthIcon className="mr-2" fontSize="small" />
                      Historical Start Month
                    </label>
                  </div>

                  <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-950/70">
                    <button
                      type="button"
                      onClick={() =>
                        setPickerYear(currentYear => currentYear - 1)
                      }
                      className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-600 transition hover:bg-white hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                      aria-label="Previous year"
                    >
                      <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                    </button>
                    <div className="min-w-22 text-center text-lg font-semibold text-slate-900 dark:text-white">
                      {pickerYear}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        canGoNextYear &&
                        setPickerYear(currentYear => currentYear + 1)
                      }
                      disabled={!canGoNextYear}
                      className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-600 transition hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
                      aria-label="Next year"
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
                    </button>
                  </div>
                </div>

                <div className="relative mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                  {MONTH_LABELS.map((label, index) => {
                    const monthNumber = index + 1;
                    const monthValue = getMonthValue(pickerYear, monthNumber);
                    const isSelected = selectedMonth === monthValue;
                    const isPartialMonth = partialEndMonthValue === monthValue;
                    const isDiscountMonth =
                      hasActivePromotion &&
                      promoDiscountThresholdMonthValue &&
                      monthValue <= promoDiscountThresholdMonthValue;
                    const isInSelectedRange =
                      Boolean(selectedMonth) &&
                      Boolean(maxMonthValue) &&
                      monthValue >= selectedMonth &&
                      monthValue <= maxMonthValue;
                    const isDisabled =
                      (maxMonthValue ? monthValue > maxMonthValue : false) ||
                      (promoEarliestMonthValue
                        ? monthValue < promoEarliestMonthValue
                        : false);

                    return (
                      <button
                        key={monthValue}
                        type="button"
                        onClick={() => {
                          if (isDisabled) return;
                          setSelectedMonth(monthValue);
                        }}
                        disabled={isDisabled}
                        className={`group relative z-10 min-h-[108px] overflow-hidden rounded-xl border px-4 py-3.5 text-left transition ${
                          isSelected
                            ? isDiscountMonth
                              ? 'border-amber-400 bg-amber-500 text-white dark:border-amber-400'
                              : 'border-[#3C50E0] bg-[#3C50E0] text-white'
                            : isInSelectedRange
                              ? isPromoDiscountApplied
                                ? 'border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-500 dark:bg-amber-950/30 dark:text-amber-100'
                                : 'border-[#3C50E0]/30 bg-[#3C50E0]/10 text-[#3C50E0] dark:border-[#3C50E0]/40 dark:bg-[#3C50E0]/15 dark:text-blue-200'
                              : isDisabled
                                ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-300 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-700'
                                : isDiscountMonth
                                  ? 'border-amber-200 bg-amber-50/90 text-amber-900 hover:border-amber-300 hover:bg-white dark:border-amber-800/70 dark:bg-amber-950/20 dark:text-amber-100 dark:hover:border-amber-600 dark:hover:bg-amber-950/30'
                                  : 'border-slate-200 bg-slate-50/80 text-slate-800 hover:border-slate-300 hover:bg-white dark:border-slate-700 dark:bg-slate-950/60 dark:text-slate-100 dark:hover:border-slate-500 dark:hover:bg-slate-900'
                        }`}
                      >
                        {!isDisabled && !isSelected && (
                          <span className="absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/60 to-transparent opacity-0 transition group-hover:opacity-100" />
                        )}
                        {isDiscountMonth && !isDisabled && (
                          <span
                            className={`absolute right-3 top-3 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${
                              isSelected
                                ? 'bg-white/20 text-white'
                                : isInSelectedRange
                                  ? 'bg-white/20 text-white'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-200'
                            }`}
                          >
                            50% Off
                          </span>
                        )}
                        {isPartialMonth && !isDisabled && (
                          <span
                            className={`absolute bottom-3 right-3 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${
                              isSelected
                                ? 'bg-white/20 text-white'
                                : 'bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-200'
                            }`}
                          >
                            Partial
                          </span>
                        )}
                        <span className="relative text-[15px] font-semibold">
                          {label}
                        </span>
                        <span
                          className={`relative mt-1 block text-[11px] uppercase tracking-[0.24em] ${
                            isSelected
                              ? 'text-white/70'
                              : isInSelectedRange
                                ? isPromoDiscountApplied
                                  ? 'text-amber-700/80 dark:text-amber-200/80'
                                  : 'text-[#3C50E0]/70 dark:text-blue-200/70'
                                : 'text-slate-400'
                          }`}
                        >
                          {pickerYear}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {shouldShowRequestOffer && (
                <div className="flex h-full flex-col gap-4 self-stretch">
                  <div
                    className={`relative flex-1 overflow-hidden rounded-xl border p-5 shadow-sm ${
                      isPromoDiscountApplied
                        ? 'border-amber-200 bg-white text-slate-900 dark:border-amber-800/60 dark:bg-slate-900 dark:text-slate-100'
                        : 'border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'
                    }`}
                  >
                    <div
                      className={`absolute inset-x-0 top-0 h-28 ${
                        isPromoDiscountApplied
                          ? 'bg-gradient-to-br from-amber-100/90 via-orange-50/70 to-transparent dark:from-amber-900/30 dark:via-orange-900/10'
                          : 'bg-gradient-to-br from-slate-100 via-white to-transparent dark:from-slate-800/60 dark:via-slate-950'
                      }`}
                    />
                    <div className="relative flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs uppercase tracking-[0.26em] text-slate-400 dark:text-slate-500">
                          Summary
                        </p>
                        <p className="mt-2 text-[1.9rem] font-semibold leading-tight text-slate-900 dark:text-white">
                          {selectedMonthLabel || 'Choose a month'}
                        </p>
                        <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                          Up to{' '}
                          {formatDateTimeUtc(
                            activeOfferBatch?.requestedEndDate ||
                              payload.currentAnalyzeStartDate
                          )}
                          {partialEndMonthValue ? ' (partial month)' : ''}
                        </p>
                      </div>
                      <div
                        className={`inline-flex min-w-[118px] justify-center rounded-full border px-3 py-1 text-center text-[11px] font-semibold uppercase tracking-[0.2em] ${
                          isPromoDiscountApplied
                            ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800/60 dark:bg-amber-900/30 dark:text-amber-200'
                            : 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                        }`}
                      >
                        {isPromoDiscountApplied ? 'Promo' : 'Standard'}
                      </div>
                    </div>
                    {selectedMonthPreview ? (
                      <div className="relative mt-5 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-950/80">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-800">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Commits
                            </p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              {formatNumber(selectedMonthPreview.commitCount)}
                            </p>
                          </div>

                          <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-800">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Tokens
                            </p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              {formatNumber(selectedMonthPreview.totalTokens)}
                            </p>
                          </div>

                          <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-800">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Cost
                            </p>
                            <p
                              className={`text-sm font-semibold ${
                                selectedMonthPreview.isDiscountApplied
                                  ? 'text-slate-400 line-through dark:text-slate-500'
                                  : 'text-slate-900 dark:text-white'
                              }`}
                            >
                              {formatCurrency(
                                selectedMonthPreview.originalChargeAmountUsd ||
                                  selectedMonthPreview.chargeAmountUsd
                              )}
                            </p>
                          </div>

                          {shouldShowDiscountRow && (
                            <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-800">
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                Discount
                              </p>
                              {selectedMonthPreview.isDiscountApplied ? (
                                <div className="flex items-center gap-3 text-right">
                                  <p className="text-xs font-medium text-amber-600 dark:text-amber-300">
                                    Save{' '}
                                    {formatCurrency(
                                      selectedMonthPreview.savingsAmountUsd
                                    )}
                                  </p>
                                  <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                    {selectedMonthPreview.discountPercentage}%
                                    off
                                  </p>
                                </div>
                              ) : (
                                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                                  Standard pricing
                                </p>
                              )}
                            </div>
                          )}

                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Total
                            </p>
                            <p
                              className={`text-xl font-semibold ${
                                isPromoDiscountApplied
                                  ? 'text-amber-600 dark:text-amber-300'
                                  : 'text-slate-900 dark:text-white'
                              }`}
                            >
                              {formatCurrency(
                                selectedMonthPreview.chargeAmountUsd
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="relative mt-5 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm dark:border-slate-700 dark:bg-slate-950/80">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-800">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Commits
                            </p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              -
                            </p>
                          </div>
                          <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-800">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Tokens
                            </p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              -
                            </p>
                          </div>
                          <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-800">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Cost
                            </p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">
                              -
                            </p>
                          </div>
                          {shouldShowDiscountRow && (
                            <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-800">
                              <p className="text-sm text-slate-500 dark:text-slate-400">
                                Discount
                              </p>
                              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                Pending selection
                              </p>
                            </div>
                          )}
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Total
                            </p>
                            <p className="text-xl font-semibold text-slate-900 dark:text-white">
                              -
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={
                        hasActivePromotion && selectedMonthPreview
                          ? openDirectStartConfirmation
                          : handleCreateRequest
                      }
                      disabled={
                        submitting ||
                        !selectedMonth ||
                        !payload.currentAnalyzeStartDate
                      }
                      className={`${BUTTON_PRIMARY_CLASS} w-full`}
                    >
                      {submitting ? (
                        hasActivePromotion && selectedMonthPreview ? (
                          <Loading type="inline" />
                        ) : (
                          <Loading type="inline" />
                        )
                      ) : hasActivePromotion && selectedMonthPreview ? (
                        'Review Payment and Start Analysis'
                      ) : (
                        'Request Historical Analysis Offer'
                      )}
                    </button>

                    {!hasActivePromotion || !selectedMonthPreview ? (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
                        No payment will be charged unless you review and approve
                        the final price first.
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      {activeRequest && (
        <div className={PANEL_CLASS}>
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div
                className={`${BADGE_LABEL_CLASS} ${selectedStatusMeta.chip}`}
              >
                <ScheduleIcon className="mr-2" fontSize="inherit" />
                {activeRequest.status.replace(/_/g, ' ')}
              </div>
              <h3 className={SECTION_TITLE_CLASS}>
                {selectedStatusMeta.title}
              </h3>
              <p className={SECTION_DESCRIPTION_CLASS}>
                {selectedStatusMeta.description}
              </p>
            </div>

            <div className="w-full max-w-xl rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-950/40">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className={PANEL_TIGHT_CLASS}>
                  <div className="inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
                    <CalendarMonthIcon className="mr-2" sx={{ fontSize: 16 }} />
                    Requested Range
                  </div>
                  <p className="mt-3 text-lg font-semibold leading-snug text-slate-900 dark:text-white">
                    {formatDate(activeRequest.requestedStartDate)}
                  </p>
                  <p className="mt-1 text-sm text-slate-400 dark:text-slate-500">
                    up to the exclusive scan boundary
                  </p>
                  <p className="mt-1 text-lg font-semibold leading-snug text-slate-900 dark:text-white">
                    {formatDateTimeUtc(activeRequest.requestedEndDate)}
                  </p>
                </div>

                <div className={PANEL_TIGHT_CLASS}>
                  <div className="inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
                    <ScheduleIcon className="mr-2" sx={{ fontSize: 16 }} />
                    Expires At
                  </div>
                  <p className="mt-3 text-lg font-semibold leading-snug text-slate-900 dark:text-white">
                    {formatDate(activeRequest.expiresAt)}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Temporary estimate data will be kept until this date.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {(activeRequest.status === 'estimate_ready' ||
            activeRequest.status === 'payment_pending' ||
            activeRequest.status === 'payment_failed' ||
            activeRequest.status === 'processing_queued' ||
            activeRequest.status === 'processing' ||
            activeRequest.status === 'completed') && (
            <div className="mt-6">{renderEstimateMetrics(activeRequest)}</div>
          )}

          {renderWaitingState(activeRequest)}

          {(activeRequest.status === 'processing_queued' ||
            activeRequest.status === 'processing' ||
            activeRequest.status === 'completed') &&
            (activeRequest?.pipelineProgress ? (
              <HistoricalPipelineProgress
                pipelineProgress={activeRequest.pipelineProgress}
              />
            ) : (
              <div className={`mt-6 ${PANEL_SOFT_CLASS}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      Progress
                    </p>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      {formatNumber(activeRequest?.progress?.completedCommits)}{' '}
                      of {formatNumber(activeRequest?.progress?.totalCommits)}{' '}
                      commits processed
                    </p>
                    {getUnanalyzedCommitsMessage(
                      activeRequest?.progress?.failedCommits
                    ) && (
                      <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                        Unanalyzed commits:{' '}
                        {formatNumber(activeRequest?.progress?.failedCommits)}
                      </p>
                    )}
                  </div>
                  <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200">
                    <AutoGraphIcon className="mr-2" fontSize="small" />
                    {Math.min(
                      100,
                      activeRequest?.progress?.totalCommits
                        ? Math.round(
                            (Number(
                              activeRequest?.progress?.completedCommits || 0
                            ) /
                              Number(
                                activeRequest?.progress?.totalCommits || 1
                              )) *
                              100
                          )
                        : activeRequest.status === 'completed'
                          ? 100
                          : 0
                    )}
                    %
                  </div>
                </div>
                <div className="mt-4 h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                  <div
                    className="h-full rounded-full bg-[#3C50E0] transition-all"
                    style={{
                      width: `${
                        Math.min(
                          100,
                          activeRequest?.progress?.totalCommits
                            ? (Number(
                                activeRequest?.progress?.completedCommits || 0
                              ) /
                                Number(
                                  activeRequest?.progress?.totalCommits || 1
                                )) *
                                100
                            : activeRequest.status === 'completed'
                              ? 100
                              : 0
                        ) || 0
                      }%`
                    }}
                  />
                </div>
              </div>
            ))}

          {(activeRequest.errorMessage ||
            activeRequest?.payment?.failureMessage) && (
            <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
              {activeRequest.errorMessage ||
                activeRequest?.payment?.failureMessage}
            </div>
          )}

          <div className="mt-6">{renderRequestActions(activeRequest)}</div>
        </div>
      )}

      {recentRequests.length > 0 && (
        <div className={PANEL_CLASS}>
          <div className="flex items-center justify-between">
            <div>
              <p className={SECTION_EYEBROW_CLASS}>History</p>
              <p className={SECTION_TITLE_CLASS}>Recent Requests</p>
              <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                Your latest historical analysis requests and their outcomes.
              </p>
            </div>
            <button
              type="button"
              onClick={() => fetchRequests(true)}
              className={BUTTON_SECONDARY_CLASS}
            >
              <RefreshRoundedIcon className="mr-2" fontSize="small" />
              Refresh
            </button>
          </div>

          <div className="mt-6 space-y-4">
            {recentRequests.map(request => (
              <div key={request.id} className={PANEL_SUBTLE_CLASS}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
                      {request.status.replace(/_/g, ' ')}
                    </p>
                    <p className="mt-3 text-sm font-semibold text-slate-900 dark:text-white">
                      {formatDate(request.requestedStartDate)} up to{' '}
                      {formatDateTimeUtc(request.requestedEndDate)}
                    </p>
                    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                      Created on {formatDate(request.createdAt)}
                    </p>
                    {getUnanalyzedCommitsMessage(
                      request?.progress?.failedCommits
                    ) && (
                      <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
                        {getUnanalyzedCommitsMessage(
                          request?.progress?.failedCommits
                        )}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-950">
                      <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                        Commits
                      </p>
                      <p className="mt-2 font-semibold text-slate-900 dark:text-white">
                        {formatNumber(request?.estimate?.commitCount)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-950">
                      <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                        Tokens
                      </p>
                      <p className="mt-2 font-semibold text-slate-900 dark:text-white">
                        {formatNumber(request?.estimate?.totalTokens)}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-950">
                      <p className="text-xs uppercase tracking-[0.22em] text-slate-400">
                        Price
                      </p>
                      <p className="mt-2 font-semibold text-slate-900 dark:text-white">
                        {formatCurrency(request?.estimate?.chargeAmountUsd)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!payload.currentAnalyzeStartDate && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          Your current analysis start date is not configured yet, so historical
          analysis requests are not available right now.
        </div>
      )}

      {!payload.currentPlanName && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
          Historical analysis pricing will use your currently active plan once
          it is available.
        </div>
      )}
    </div>
  );
};

export { HistoricalAnalysis };
export default HistoricalAnalysis;
